"use client"

import type React from "react"
import { createContext, useContext, useState } from "react"
import type { Locale } from "./types"
import { translations } from "./i18n"

type LocaleContextType = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("app-locale") as Locale
      if (stored === "en" || stored === "es") {
        return stored
      }
    }
    return "es"
  })

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("app-locale", newLocale)
      } catch (error) {
        console.error("Error saving locale to localStorage:", error)
      }
    }
  }

  const t = (key: string, params?: Record<string, string | number>): string => {
    const keys = key.split(".")
    let value: any = translations[locale]

    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k]
      } else {
        return key
      }
    }

    if (typeof value === "string") {
      if (params) {
        return value.replace(/\{(\w+)\}/g, (_, param) => String(params[param] ?? `{${param}}`))
      }
      return value
    }

    return key
  }

  return <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (context === undefined) {
    throw new Error("useLocale must be used within a LocaleProvider")
  }
  return context
}
